// CommentThread — read + post comments on a candidate (PRD §9.5 + §9.7,
// docs/frontend_architecture.md §6).
//
// Two surfaces, one component:
//   - `mode: 'share'`: public share-link viewer (PRD §9.5, B2d). Reads
//     and posts via `useShareComments` / `usePostShareComment`. Honors
//     `canComment` / `canCommentAnonymously` (anonymous viewers see a
//     display-name input).
//   - `mode: 'authed'`: authed CandidateDetail drawer (PRD §9.7, B4e2).
//     Reads and posts via `useComments` / `usePostComment`. The JWT
//     identifies the author; no display-name input.
//
// The render output is identical between modes — only the data hook
// pair swaps. Discriminated-union props let TS narrow correctly at the
// call site so the two callers can't pass each other's required fields
// by accident.
//
// Optimistic UX is owned by the mutation hooks themselves (share +
// authed both encode prepend / rollback / swap-in-place). The form
// here only manages local input state and clears `body` on success.

import { useState, type FormEvent } from 'react';

import { apiErrorMessage } from '../api/client';
import {
  usePostShareComment,
  type UsePostShareCommentVars,
} from '../api/mutations/share';
import {
  usePostComment,
  type UsePostCommentVars,
} from '../api/mutations/review';
import { useShareComments } from '../api/queries/share';
import { useComments } from '../api/queries/review';
import type { CommentOut } from '../api/queries/review';
import { useAuthStore } from '../stores/authStore';
import { groupByFrame, type FrameGroup } from './commentGroups';
import { Skeleton } from './Skeleton';

interface CommonProps {
  candidateId: string;
  canComment: boolean;
}

interface ShareModeProps extends CommonProps {
  mode: 'share';
  token: string;
  canCommentAnonymously: boolean;
  password?: string | null;
}

interface AuthedModeProps extends CommonProps {
  mode: 'authed';
  orgSlug: string;
  projectId: string;
}

type CommentThreadProps = ShareModeProps | AuthedModeProps;

// Hook-call shape that both modes converge on. Each branch invokes its
// own hook pair below — calling both hooks in one component would
// violate the rules of hooks, so each mode lives in its own
// `<XModeBody>` and the top-level component dispatches.
interface ThreadDataSource {
  list: { data: CommentOut[] | undefined; isPending: boolean; isError: boolean };
  postBody: (vars: { body: string; author_display_name?: string }) => Promise<void>;
  postPending: boolean;
  postError: unknown;
}

export function CommentThread(props: CommentThreadProps): React.JSX.Element {
  if (props.mode === 'share') {
    return <ShareCommentThread {...props} />;
  }
  return <AuthedCommentThread {...props} />;
}

function ShareCommentThread(props: ShareModeProps): React.JSX.Element {
  const password = props.password ?? null;
  const list = useShareComments(props.token, props.candidateId, password);
  const post = usePostShareComment(props.token, props.candidateId, password);
  const source: ThreadDataSource = {
    list: {
      data: list.data,
      isPending: list.isPending,
      isError: list.isError,
    },
    postBody: async ({ body, author_display_name }) => {
      const vars: UsePostShareCommentVars = { body };
      if (author_display_name !== undefined) {
        vars.author_display_name = author_display_name;
      }
      await post.mutateAsync(vars);
    },
    postPending: post.isPending,
    postError: post.error,
  };
  return (
    <ThreadView
      source={source}
      canComment={props.canComment}
      canCommentAnonymously={props.canCommentAnonymously}
    />
  );
}

function AuthedCommentThread(props: AuthedModeProps): React.JSX.Element {
  const list = useComments(props.orgSlug, props.projectId, props.candidateId);
  const post = usePostComment(props.orgSlug, props.projectId, props.candidateId);
  const source: ThreadDataSource = {
    list: {
      data: list.data,
      isPending: list.isPending,
      isError: list.isError,
    },
    postBody: async ({ body }) => {
      const vars: UsePostCommentVars = { body };
      await post.mutateAsync(vars);
    },
    postPending: post.isPending,
    postError: post.error,
  };
  return (
    <ThreadView
      source={source}
      canComment={props.canComment}
      canCommentAnonymously={false}
    />
  );
}

function ThreadView({
  source,
  canComment,
  canCommentAnonymously,
}: {
  source: ThreadDataSource;
  canComment: boolean;
  canCommentAnonymously: boolean;
}): React.JSX.Element {
  const signedIn = useAuthStore((s) => s.user !== null);

  const [body, setBody] = useState('');
  const [displayName, setDisplayName] = useState('');

  // The display-name input only matters on share-mode + anonymous-
  // allowed links + anonymous viewer. The authed mode collapses
  // `canCommentAnonymously` to false unconditionally.
  const showDisplayName = canComment && canCommentAnonymously && !signedIn;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed.length === 0) return;
    try {
      if (showDisplayName && displayName.trim().length > 0) {
        await source.postBody({
          body: trimmed,
          author_display_name: displayName.trim(),
        });
      } else {
        await source.postBody({ body: trimmed });
      }
      setBody('');
    } catch {
      // Error renders below from source.postError; mutateAsync
      // rejects so the caller can await but the rejection value
      // isn't needed here.
    }
  }

  const errorDetail = source.postError
    ? apiErrorMessage(source.postError, 'Could not post comment.')
    : null;

  return (
    <section
      aria-label="Comments"
      className="border-border mt-7 flex flex-col gap-4 border-t pt-6"
    >
      <h2 className="text-text text-15 font-semibold">Comments</h2>

      {source.list.isPending && <Skeleton className="h-20 w-full" />}
      {source.list.isError && (
        <p role="alert" className="text-danger text-13">
          Could not load comments.
        </p>
      )}
      {source.list.data !== undefined && (
        <CommentList groups={groupByFrame(source.list.data)} />
      )}

      {canComment ? (
        <form
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          noValidate
          className="flex flex-col gap-3"
          aria-label="Post comment"
        >
          {showDisplayName && (
            <div className="flex flex-col gap-1">
              <label htmlFor="comment-author" className="text-text-muted text-13">
                Display name
              </label>
              <input
                id="comment-author"
                type="text"
                autoComplete="off"
                maxLength={200}
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                }}
                className="bg-bg-elevated text-text border-border rounded-2 focus:border-accent border px-3 py-2 text-13 outline-none"
              />
              <p className="text-text-faint text-11">
                Optional — leave blank to post as Anonymous.
              </p>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label htmlFor="comment-body" className="text-text-muted text-13">
              Comment
            </label>
            <textarea
              id="comment-body"
              required
              rows={3}
              maxLength={10_000}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
              }}
              className="bg-bg-elevated text-text border-border rounded-2 focus:border-accent border px-3 py-2 text-13 outline-none"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={source.postPending || body.trim().length === 0}
              className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi border px-3 py-2 text-13 transition-colors disabled:opacity-60"
            >
              {source.postPending ? 'Posting…' : 'Post comment'}
            </button>
            {errorDetail !== null && (
              <p role="alert" className="text-danger text-11">
                {errorDetail}
              </p>
            )}
          </div>
        </form>
      ) : (
        <p className="text-text-faint text-13">
          Comments are disabled on this share link.
        </p>
      )}
    </section>
  );
}

interface CommentListProps {
  groups: FrameGroup[];
}

function CommentList({ groups }: CommentListProps): React.JSX.Element {
  if (groups.length === 0) {
    return (
      <p className="text-text-faint text-13">No comments yet.</p>
    );
  }
  return (
    <ol className="flex flex-col gap-5" aria-label="Comment list">
      {groups.map((g) => (
        <li key={g.frame === null ? 'general' : `frame-${g.frame.toString()}`}>
          <p
            className="text-text-faint text-11 mb-2 uppercase tracking-wider"
            aria-label="Frame group"
          >
            {g.frame === null ? 'General' : `Frame ${g.frame.toString()}`}
          </p>
          <ol className="flex flex-col gap-3">
            {g.comments.map((c) => (
              <li
                key={c.id}
                className="border-border bg-bg-elevated/40 rounded-2 border px-3 py-2"
              >
                <p className="text-text text-13 font-semibold">
                  {c.author_display_name}
                </p>
                <p className="text-text mt-1 whitespace-pre-wrap text-13">
                  {c.body}
                </p>
              </li>
            ))}
          </ol>
        </li>
      ))}
    </ol>
  );
}
